import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Layout } from "./components/Layout";
import { api, ApiError } from "./lib/api";
import { AdminPage } from "./pages/AdminPage";
import { ComparePage } from "./pages/ComparePage";
import { JoinTeamPage } from "./pages/JoinTeamPage";
import { LeaderboardPage } from "./pages/LeaderboardPage";
import { LoginPage } from "./pages/LoginPage";
import { SessionDetailPage } from "./pages/SessionDetailPage";
import { SessionsPage } from "./pages/SessionsPage";
import { TeamDetailPage } from "./pages/TeamDetailPage";
import { TeamsPage } from "./pages/TeamsPage";
import { UploadPage } from "./pages/UploadPage";

export default function App() {
  const { t } = useTranslation();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    // A 401 is the normal signed-out answer, not a transient failure.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 401) && failureCount < 2,
  });

  if (me.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-zinc-500">
        {t("app.loading")}
      </div>
    );
  }

  const signedIn = me.isSuccess;

  return (
    <Routes>
      <Route
        path="/login"
        element={signedIn ? <Navigate to="/" replace /> : <LoginPage />}
      />

      {/* Outside the sign-in gate: an invite has to be readable by someone who has
          never been here, or the link says nothing until they have already logged in. */}
      <Route path="/join/:token" element={<JoinTeamPage />} />

      {signedIn ? (
        <Route
          path="*"
          element={
            <Layout user={me.data}>
              <Routes>
                <Route path="/" element={<SessionsPage />} />
                <Route path="/upload" element={<UploadPage />} />
                {me.data?.is_admin && <Route path="/admin" element={<AdminPage />} />}
                <Route path="/sessions/:id" element={<SessionDetailPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/compare" element={<ComparePage />} />
                <Route path="/teams" element={<TeamsPage />} />
                <Route path="/teams/:id" element={<TeamDetailPage />} />
                <Route path="/teams/:id/compare" element={<ComparePage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          }
        />
      ) : (
        <>
          {/* The public board is the one page worth showing a stranger, so it is
              also the one page the sign-in gate lets past. */}
          <Route
            path="/leaderboard"
            element={
              <Layout>
                <LeaderboardPage />
              </Layout>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      )}
    </Routes>
  );
}
